<template>
  <div class="room-card border-b-1 border-grey-lighter mb-40 md:mb-80" :data-room-id="room.id">
    <h4 class='mb-16 text-grey-darkest'>{{ room.name }}</h4>
    <div class="room-card__brief flex flex-col md:flex-row flex-wrap">
      <div class="room-card__brief__gallery w-full md:w-1/2 lg:w-full xl:w-1/2 relative">
        <template v-if="isMobile && isImage">
          <imageSwiper :photos="room.images" @showPhoto="openPhotoSwipe" />
        </template>
        <template v-else>
          <div class="w-full h-full bg-cover bg-center bg-grey-lightest room-card__brief__gallery__cover" :style="roomCover" @click.stop="openPhotoSwipe()">
            <div class="room-card__brief__gallery__cover__mask" v-if="isImage">
              <i class="owl-search"></i>
            </div>
          </div>
        </template>
        <photoSwipe v-if="isImage" ref="photoSwipe" :photos="room.images" @close="photoSwipeCloseHandler"></photoSwipe>
      </div>
      <div class="room-card__brief__content w-full md:w-1/2 lg:w-full xl:w-1/2 md:pl-24 pb-16">
        <div class="flex items-center" :class="roomStockCount ? 'justify-between mb-24' : 'justify-end'">
          <div v-if="roomStockCount" class="text-sm bg-grey-lighter p-8 rounded-lg">{{ roomStockCount }}</div>
          <div class="room-card__brief__content__price-tag os-tag os-tag-full os-tag-outline os-tag-grey">
            <small class='mr-4'>{{ selectedCurrency }}</small>
            <span>{{ room.lowest_price | currency }}</span>
            <small class='ml-4'>{{ $t('up') }}</small>
          </div>
        </div>
        <div class="room-card__brief__content__row ml-4">
          <div class="content-group">
            <label>{{ $t('bedType') }}</label>
            <div v-for="(bed, index) in room.beds" :key="index">
              {{ bed.name }} x {{ bed.count }}
            </div>
          </div>
        </div>
        <div class="room-card__brief__content__row flex ml-4">
          <div class="content-group mr-24" v-if="room.max_people">
            <label>{{ $t('peopleLimit') }}</label>
            <span>{{ room.max_people }} {{ $tc('person', room.max_people) }}</span>
          </div>
          <div class="content-group" v-if="room.area">
            <label>{{ $t('area') }}</label>
            <span>{{ room.area }} {{ room.area_unit }}</span>
          </div>
        </div>
        <div class="room-card__brief__content__row">
          <div class="content-group"  v-if="isFeatures">
            <label>{{ $tc('features', top4features.length) }}</label>
            <ul class='feature-list flex flex-wrap mb-8'>
              <li
                v-for="(feature, index) in top4features"
                :key="index"
                class="mt-4 mr-8"
              >
                <img
                  v-if="feature.icon_url"
                  :src="feature.icon_url"
                  class="room-card__feature-icon"
                />
                <i
                  v-else-if="feature.name"
                  class="be-icon"
                  :class="feature.name"
                />
                <span class="align-text-top">
                  {{ feature.text }}
                </span>
              </li>
            </ul>
            <a
              v-if='isFeatureMoreThan4'
              class='more-features text-sm cursor-pointer'
              @click='showMoreFeatures'>
              <i class="owl-status-add mr-4"></i>
              <span>{{ $t('more') }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class='room-card_plans'>
      <RoomPlan
        v-for="plan in sortedPlans"
        :key="plan.id"
        :plan="plan"
        :room="room"
        :stock="room.stock_count"
        :addedStock="addedStock"
        @stockChange="hasAdded"
        v-on="$listeners"
      >
      </RoomPlan>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import RoomPlan from '@/views/Hotel/components/roomPlan'
// import swiper from '@/components/swiper'
import roomDefaultCover from '@/assets/img/default-room.jpg'
import photoSwipe from '@/components/photoSwipe'
import contentWithCover from '@/components/modals/contentWithCover'
import imageSwiper from '@/components/imageSwiper'
import { mapGetters } from 'vuex'

export default {
  name: 'room-card',
  components: {
    RoomPlan,
    // swiper,
    photoSwipe,
    imageSwiper
  },
  props: {
    room: {
      type: Object
    }
  },
  data () {
    return {
      addedTotal: [],
      roomDefaultCover: roomDefaultCover
    }
  },
  computed: {
    ...mapGetters([
      'windowWidth',
      'hotel/hotelDisplaySetting',
      'selectedCurrency'
    ]),
    isImage: function () {
      return this.room.images && this.room.images.length > 0
    },
    roomCover: function () {
      return {
        backgroundImage: this.isImage ? `url('${this.room.images[0]}')` : `url('${this.roomDefaultCover}')`
      }
    },
    isFeatures: function () {
      return !_.isEmpty(this.room.features)
    },
    top4features: function () {
      return this.room.features.slice(0, 4)
    },
    isFeatureMoreThan4: function () {
      return this.room.features.length > 4
    },
    addedStock: function () {
      return _.sumBy(this.addedTotal, stock => stock.count)
    },
    sortedPlans: function () {
      return this.room.plans // ? redmine #2794
    },
    isMobile () {
      return this.windowWidth < 768
    },
    roomStockCount () {
      const showStockCountOnList = this['hotel/hotelDisplaySetting']?.showStockCountOnList
      const minStock = this.room.stock_count?.length ? Math.min(...this.room.stock_count.map(s => s.count)) : 0
      return showStockCountOnList && this.room && minStock > 0 ? this.$t('remainRoom', { stock: minStock, unit: this.room.unit }) : undefined
    }
  },
  methods: {
    hasAdded: function (id, count) {
      const isAdded = _.findIndex(this.addedTotal, total => total.id === id)
      if (isAdded > -1) {
        this.addedTotal[isAdded].count = count
      } else {
        this.addedTotal.push({
          id: id,
          count: count
        })
      }
    },
    openPhotoSwipe: function (info) {
      if (this.isImage) {
        this.$refs.photoSwipe.open(info)
        this.hotelCoverShowHandler(true)
      }
    },
    showMoreFeatures: function () {
      this.$modal.show(contentWithCover, {
        isFeatures: true,
        features: this.room.features
      },
      {
        adaptive: true,
        name: 'features-modal',
        height: 'auto',
        width: '100%',
        maxWidth: 450,
        scrollable: true
      })
    },
    photoSwipeCloseHandler () {
      this.hotelCoverShowHandler(false)
    },
    hotelCoverShowHandler (value) {
      const hotelMain = document.querySelector('.hotel-main.main')
      const hotelRooms = document.querySelector('.hotel-main.main .hotel-rooms')
      if (hotelMain && hotelRooms) {
        if (value) {
          hotelMain.classList.remove('show')
          hotelRooms.classList.add('show')
        } else {
          hotelRooms.classList.remove('show')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.room-card {
  &.active {
    position: relative;
    border: 2px solid #000;
    border-radius: 8px;
    padding: 0.35em 0.75em;
    margin-top: 2em;

    &:before {
      content: attr(data-before-title);
      position: absolute;
      top: -2em;
      left: 0;
      width: 100%;
      background: linear-gradient(0.5turn, #fff 40%, #d1d7dd 50%, #fff 40%);
      padding-right: 0.5em;
      font-weight: bold;
    }

    &:after {
      content: attr(data-after-title);
      position: absolute;
      bottom: -4em;
      left: 0;
      width: 100%;
      background: linear-gradient(0.5turn, #fff 40%, #d1d7dd 50%, #fff 40%);
      padding-right: 0.5em;
      font-weight: bold;
    }
  }
}
</style>

<style lang='sass' scoped>
@import "@/stylesheets/_function.sass"

.room-card__brief
  @apply relative
  &__gallery
    // width: 420px
    @apply border-1 flex-no-shrink
    height: fit-content
    // @screen md
    //   width: 50%
    // @screen lg
    //   @apply w-full
    // @screen xl
    //   width: 50%
    &__cover
      padding-top: imageRatio(400, 270)
      &:hover .room-card__brief__gallery__cover__mask
        @apply opacity-100
        i
          @apply opacity-100
      &__mask
        @apply absolute w-full h-full flex items-center justify-center opacity-0 cursor-pointer
        top: 0
        left: 0
        transition: opacity 0.3s ease-out
        background: radial-gradient(ellipse at center, rgba(0,0,0,0.20) 0%,rgba(0,0,0,0.55) 100%)
        i
          @apply text-white text-6xl opacity-0
          transition: opacity 0.3s ease-out
  &__content__price-tag
    @apply border-grey-lighter text-xs cursor-default #{!important}
    right: 1.5rem
    top: 0px

  &__content__row
    @apply text-sm
    &:not(:last-of-type)
      .content-group
        @apply mb-24
    .content-group
      label
        @apply text-grey-dark block mb-4
      span, div
        @apply text-grey-darker

    .feature-list
      padding-left: 0rem
      list-style-type: none
      @apply w-full
      li
        @apply mb-4 text-grey-darker
        .room-card__feature-icon
          height: 18px
          width: 18px

    .more-features
      span, i
        @apply text-blue
</style>
